import React, { useMemo } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import { formatMoneyRMB, formatMoneyUSD } from '../../utils/functions';
import { Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface Props {
  settlement: Settlement;
  totalFob: number;
  totalKgs: number;
  boxSize?: number;
  /** Exporter-standard cost in USD per detail id; replaces the FM costs. */
  detailCostsUsd?: Record<number, number>;
}

interface GroupedFOB {
  variety: string;
  caliber: string;
  color: string;
  totalKilos: number;
  totalLocalPrice: number;
  weightedLocalPricePerBox: number;
  totalFob: number;
  fobPerBox: number;
  fobPerKg: number;
}

const LiquidationFOBTable: React.FC<Props> = ({
  settlement,
  totalFob,
  totalKgs,
  boxSize = 5,
  detailCostsUsd,
}) => {
  const { t } = useTranslation('settlements');
  const groupedData = useMemo(() => {
    const groups: Record<string, GroupedFOB> = {};

    settlement.settlement_details?.forEach((detail) => {
      const key = `${detail.variety}-${detail.caliber}-${detail.color}`;

      if (!groups[key]) {
        groups[key] = {
          variety: detail.variety,
          caliber: detail.caliber,
          color: detail.color,
          totalKilos: 0,
          totalLocalPrice: 0,
          weightedLocalPricePerBox: 0,
          totalFob: 0,
          fobPerBox: 0,
          fobPerKg: 0,
        };
      }

      groups[key].totalKilos += detail.kilos;
      groups[key].totalLocalPrice += detail.local_total_price;

      // Calculamos el FOB específico para este detalle
      const detailFob = detailCostsUsd
        ? detail.local_total_price / settlement.exchange_rate! -
          (detailCostsUsd[detail.id] || 0)
        : (detail.local_total_price -
            detail.local_total_tax -
            detail.local_other_costs -
            (detail.local_repacking_costs || 0) -
            detail.local_total_commision) /
            settlement.exchange_rate! -
          detail.usd_freight;
      groups[key].totalFob += detailFob;
    });

    // Calcular promedios y valores por caja/kg para cada grupo
    Object.values(groups).forEach((group) => {
      // Precio local ponderado por caja equivalente
      group.weightedLocalPricePerBox =
        (group.totalLocalPrice / group.totalKilos) * boxSize;
      // FOB por caja equivalente
      group.fobPerBox = (group.totalFob / group.totalKilos) * boxSize;
      // FOB por kg
      group.fobPerKg = group.totalFob / group.totalKilos;
    });

    return Object.values(groups).sort((a, b) => b.fobPerBox - a.fobPerBox);
  }, [settlement, boxSize, detailCostsUsd]);

  const columns = [
    { name: t('VARIEDAD'), uid: 'variety' },
    { name: t('CALIBRE'), uid: 'caliber' },
    { name: t('COLOR'), uid: 'color' },
    { name: t('KILOS'), uid: 'totalKilos' },
    { name: t('PRECIO VENTA CEQ ({currency})').replace('{currency}', settlement?.local_currency || ''), uid: 'weightedLocalPricePerBox' },
    { name: t('FOB CEQ (USD)'), uid: 'fobPerBox' },
    { name: t('FOB/KG (USD)'), uid: 'fobPerKg' },
  ];

  const renderCell = (item: GroupedFOB, columnKey: string) => {
    switch (columnKey) {
      case 'totalKilos':
        return (
          <p className='text-sm'>{item.totalKilos.toLocaleString('es-CL')}</p>
        );
      case 'weightedLocalPricePerBox':
        return (
          <p className='text-sm'>
            {formatMoneyRMB(item.weightedLocalPricePerBox, {
              currency: settlement.local_currency,
            })}
          </p>
        );
      case 'fobPerBox':
        return <p className='text-sm'>{formatMoneyUSD(item.fobPerBox)}</p>;
      case 'fobPerKg':
        return <p className='text-sm'>{formatMoneyUSD(item.fobPerKg)}</p>;
      default:
        return <p className='text-sm'>{item[columnKey as keyof GroupedFOB]}</p>;
    }
  };

  return (
    <Table
      removeWrapper
      aria-label={t('Tabla de FOB por Variedad, Calibre y Color')}
      className='shadow-none max-w-screen overflow-x-auto'
    >
      <TableHeader columns={columns}>
        {(column) => <TableColumn key={column.uid}>{column.name}</TableColumn>}
      </TableHeader>
      <TableBody items={groupedData}>
        {(item) => (
          <TableRow key={`${item.variety}-${item.caliber}-${item.color}`}>
            {(columnKey) => (
              <TableCell>{renderCell(item, columnKey)}</TableCell>
            )}
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};

export default LiquidationFOBTable;
